import PDP from '@/scripts/libs/pdp'
import { Availability, CarouselItem, Product } from '@/types/pdp'
import { LIGHT_GRAY } from '@/scripts/constants/colours'
import wcs from '@/scripts/libs/wcs'
import {
  ELECTRONICS,
  NON_POLARIZED,
  POLARIZED,
  ROX_ELECTRONICS,
  SUN,
  TRANSITIONS,
} from './constants/pdp'
import {
  WcsPdpAvailability,
  WcsPdpCarouselItem,
  WcsPdpProduct,
  WcsPdpGeofitOrGiftCard,
  WcsPdpGeofit,
} from './interfaces/normalizer'
import {
  mapBridgeSize,
  mapElectronicsAudioSystem,
  mapElectronicsConnectivity,
  mapImagesGallery,
} from './libs/products'
import { GIFTCARDS } from '@/scripts/constants/store'
import { PlpProduct } from '@/types/plp'

/* TODO: Move this function inside Media module  */
export function normalizeImageBackground(imageUrl: string): string {
  if (!imageUrl) {
    return imageUrl
  }

  if (imageUrl && imageUrl.endsWith('.png')) {
    return imageUrl
  }

  if (imageUrl.indexOf('?') === -1) {
    return imageUrl + `?bgc=${LIGHT_GRAY}`
  }
  return imageUrl + `&bgc=${LIGHT_GRAY}`
}

const { productDetailsTitle } = wcs.getStoreConfig()

const isWcsPdpGeofit = (
  wcsPdpGeofitOrGiftCard: WcsPdpGeofitOrGiftCard
): wcsPdpGeofitOrGiftCard is WcsPdpGeofit => {
  return 'key' in wcsPdpGeofitOrGiftCard
}

const sortCatEntries = (a: WcsPdpGeofitOrGiftCard, b: WcsPdpGeofitOrGiftCard) => {
  const catA = a.catEntryId
  const catB = b.catEntryId

  if (isWcsPdpGeofit(a)) {
    if (catA < catB) {
      return -1
    }
    if (catA > catB) {
      return 1
    }
    return 0
  } else {
    if (catA > catB) {
      return -1
    }
    if (catA < catB) {
      return 1
    }
    return 0
  }
}

const getGeofitKey = (geofitKey: string): string => {
  const lowerCase = geofitKey?.toLowerCase()
  const replaced = lowerCase?.replace(/ /g, '_')
  return replaced
}

export const normalizePdpProduct = (srcData: WcsPdpProduct): Product => ({
  altText: srcData?.altText,
  bridgeAndNosepads: srcData?.bridgeAndNosepads,
  catEntryId: srcData?.catEntryId,
  groupKey: srcData?.groupKey,
  moCo: srcData?.moCo,
  clen: srcData?.clen,
  customizeUrl: srcData?.customizeUrl,
  frameFit: srcData?.frameFit,

  // TODO: to be confirmed
  gender: srcData?.genderValueIdentifier,

  imageUrl: srcData?.imageUrl,
  imagesGallery: mapImagesGallery(srcData),
  modelCode: srcData?.modelCode,
  modelName: GIFTCARDS.includes(srcData?.type)
    ? srcData?.resolvedSkuGridLensBridgeVal
    : srcData?.modelName,
  pageUrl: srcData?.pageUrl,
  parentCatEntryId: srcData?.parentCatEntryId,
  partNumber: srcData?.partNumber,
  productFrameColor: srcData?.productFrameColor,
  productFrameModel: srcData?.productFrameModel,
  productId: srcData?.productId,
  productPartNumber: srcData?.productPartNumber,
  resolvedSkuGridLensBridgeVal: srcData?.resolvedSkuGridLensBridgeVal,
  roxFrameCatEntryId: srcData?.roxFrameCatEntryId,
  type: srcData?.type,
  afterPay: {
    maxPrice: srcData?.afterPay?.maxPrice,
    instalments: srcData?.afterPay?.instalments,
    minPrice: srcData?.afterPay?.minPrice,
    enabled: srcData?.afterPay?.enabled,
  },
  correlatedLenses: srcData?.correlatedLenses?.map((correlatedLens) => ({
    catEntryId: correlatedLens?.catEntryId,
    draggableClen: correlatedLens?.draggableClen,
    type: correlatedLens?.type,
  })),
  currentLens:
    srcData?.correlatedLenses &&
    srcData?.correlatedLenses.find((lens) => lens.catEntryId === srcData?.catEntryId),
  polarizedLens:
    srcData?.correlatedLenses && PDP.findLensByType(srcData?.correlatedLenses, POLARIZED),
  nonPolarizedLens:
    srcData?.correlatedLenses && PDP.findLensByType(srcData?.correlatedLenses, NON_POLARIZED),
  features: {
    coorefcost: srcData?.features?.coorefcost,
    finish: srcData?.features?.finish,
    title: productDetailsTitle,
    frame: {
      color: srcData?.features?.frame?.color,
      frontOuterColor: srcData?.features?.frame?.frontOuterColor,
      description: srcData?.features?.frame?.description,
      materialPrimary: srcData?.features?.frame?.materialPrimary,
      materialSecondary: srcData?.features?.frame?.materialSecondary,
      shape: srcData?.features?.frame?.shape,
      bridgeSize: mapBridgeSize(srcData?.features?.frame?.bridgeSize),
      templeLength: srcData?.features?.frame?.templeLength,
      templeColor: srcData?.features?.frame?.templeColor,
    },

    lenses: {
      color: srcData?.features?.lenses?.color,
      description: srcData?.features?.lenses?.description,
      colorCategory: srcData?.features?.lenses?.colorCategory,
      colorCategoryIdentifier: srcData?.features?.lenses?.colorCategoryIdentifier,
      height: srcData?.features?.lenses?.height,
      treatment: srcData?.features?.lenses?.treatment,
      hasDontDriveDisclaimer: srcData?.isDriving === undefined ? false : !srcData?.isDriving,
    },

    electronics: {
      assistantType: srcData?.features?.electronics?.assistantType,
      audioSystem: mapElectronicsAudioSystem(srcData?.features?.electronics?.audioSystem),
      connectivity: mapElectronicsConnectivity(srcData?.features?.electronics?.connectivity),
      osCompatibility: srcData?.features?.electronics?.osCompatibility,
      touch: srcData?.features?.electronics?.touch,
      voiceControl: srcData?.features?.electronics?.voiceControl,

      acquisition: {
        image: srcData?.features?.electronics?.acquisition?.image,
        video: srcData?.features?.electronics?.acquisition?.video,
      },

      battery: {
        duration: srcData?.features?.electronics?.battery?.duration,
        type: srcData?.features?.electronics?.battery?.type,
      },

      memory: {
        capacity: srcData?.features?.electronics?.memory?.capacity,
        size: srcData?.features?.electronics?.memory?.size,
      },
    },
  },
  klarna: {
    enabled: srcData?.klarna?.enabled,
  },
  prescriptionThresholds: {
    powerCombinedMin: srcData?.prescriptionThresholds?.powerCombinedMin,
    powerCombinedMax: srcData?.prescriptionThresholds?.powerCombinedMax,
  },

  prices: {
    brazilInstallmentPrice: srcData?.prices?.brazilInstallmentPrice,
    currencySymbol: srcData?.prices?.currencySymbol,
    currency: srcData?.prices?.currency,
    listPrice: srcData?.prices?.listPrice,
    priceFormat: srcData?.prices?.priceFormat,
  },

  promotions: srcData?.promotions?.map((promotion) => ({
    badge: promotion?.badge,
    endDate: promotion?.endDate,
    name: promotion?.name,
    price: promotion?.price,
    priceLabel: promotion?.priceLabel,
    priority: promotion?.priority,
    startDate: promotion?.startDate,
    type: promotion?.type,
    unformattedAmount: promotion?.unformattedAmount,
    userSegments: promotion?.userSegments,
    brazilInstallmentPrice: promotion?.brazilInstallmentPrice,
    catgroups: promotion?.catgroups,
  })),

  sizes: srcData?.sizes?.map((size) => ({
    size: size?.size,
    catEntryId: size?.catEntryId,
    label: size?.label,
    isMostSoldSize: size?.isMostSoldSize,
    hingeDistance: size?.hingeDistance,
  })),

  geofits: srcData?.geoFit
    ?.map((geofit) => ({
      catEntryId: geofit?.catEntryId,
      label: geofit?.label,
      key: getGeofitKey(geofit?.key),
    }))
    .sort(sortCatEntries),

  isJunior: !!srcData?.isJunior,
  isLightJson: !!srcData?.isLightJson,
  isPolarized: !!srcData?.isPolarized,
  isPreOrder: !!srcData?.isPreOrder,
  isRoxable: !!srcData?.isRoxable,
  isSemiPrivateSale: !!srcData?.isSemiPrivateSale,
  isSupernova: !!srcData?.isSupernova,
  isTryLensEnabled: [SUN, ELECTRONICS, ROX_ELECTRONICS].includes(srcData?.type),
  isWebExclusive: !!srcData?.isWebExclusive,
  isLimitedEdition: !!srcData?.isLimitedEdition,
  isPreRelease: !!srcData?.isPreRelease,
  isNew: !!srcData?.isNew,
  isCollectionSustainable: !!srcData?.isCollectionSustainable,
  isTransition: srcData?.features?.lenses?.treatment === TRANSITIONS,
  isCaseSmart: !!srcData?.isCaseSmart,
})

export const normalizeCarousel = (carousel: WcsPdpCarouselItem[]): CarouselItem[] => {
  if (carousel) {
    return carousel.map((item) => ({
      altText: item?.altText ?? '',
      catEntryId: item?.catEntryId ?? '',
      variants: item?.variants ?? [],
      correlatedCatEntryIds: item?.variants.map((variant) => variant.catEntryId) ?? [],
      draggableClen: item?.draggableClen ?? '',
      frameColor: item?.frameColor ?? '',
      frameFinishing: item?.frameFinishing ?? '',
      hasColoredLenses: item?.hasColoredLenses ?? false,
      imageUrl: item?.imageUrl ?? '',
      lensColor: item?.lensColor ?? '',
      nonPolarizedCatEntryId: item?.nonPolarizedCatEntryId ?? '',
      partNumber: item?.partNumber ?? '',
      polarizedCatEntryId: item?.polarizedCatEntryId ?? '',
      position: item?.position ?? 0,
      promotions: item?.promotions ?? [],
      lightCondition: item?.lightCondition,
      giftCardType: item?.giftCardType,
      giftCardAmounts:
        item?.giftCardSizes?.map((size) => ({
          size: size?.size,
          catEntryId: size?.catEntryId,
          label: size?.label,
          isMostSoldSize: false,
          hingeDistance: size?.hingeDistance,
        })) ?? [],
      productPartNumber: item?.productPartNumber ?? '',
    }))
  }
  return []
}

export const normalizeProductAvailability = (
  availabilityData: WcsPdpAvailability
): Availability => ({
  // TODO: check this property with BE service
  hurryUpQuantity: +availabilityData?.hurryUpQuantity || undefined,

  inventory: availabilityData?.inventory,
  message: availabilityData?.message && PDP.sanitizeAvailabilityMessage(availabilityData?.message),
  messageRX:
    availabilityData?.messageRX && PDP.sanitizeAvailabilityMessage(availabilityData?.messageRX),
  isAvailable: Number(availabilityData?.inventory) > 0,
  isAddToBagEnabled: !!availabilityData?.isAddToBagEnabled,
  isApplePayEnabled: !!availabilityData?.isApplePayEnabled,
  isCNCEnabled: !!availabilityData?.isCNCEnabled,
  isEmailMeEnabled: !!availabilityData?.isEmailMeEnabled,
  isFISEnabled: !!availabilityData?.isFISEnabled,
  isSTSEnabled: !!availabilityData?.isSTSEnabled,
  lifecycleVal: !!availabilityData?.lifecycleVal,
  isComingSoon: !!availabilityData?.isComingSoon,
  oosStatus: availabilityData?.oosStatus,
})

export const normalizeFeaturedProduct = (srcData: Product): PlpProduct => ({
  catEntryId: srcData?.catEntryId,
  upc: srcData?.productId,
  category: srcData?.type,
  partNumber: srcData?.partNumber,
  sku: '',
  colors: [
    {
      algoliaTracking: {
        grouped: {
          indexName: 'products',
          objectIDs: [srcData?.catEntryId],
        },
        ungrouped: {
          indexName: 'products',
          objectIDs: [srcData?.catEntryId],
        },
      },
      price: srcData?.prices?.listPrice,
      promotions: srcData?.promotions,
      catEntryId: srcData?.catEntryId,
      sustainable: !!srcData?.isCollectionSustainable,
      customizable: srcData?.customizeUrl,
      polarized: srcData?.isPolarized,
      preorder: srcData?.isPreOrder,
      electronics: srcData?.type === ELECTRONICS,
      name: srcData?.modelName,
      partNumber: srcData?.partNumber,
      url: srcData?.pageUrl,
      onSale: srcData?.isNew,
      images: {
        primary: normalizeImageBackground(srcData?.imageUrl),
        secondary: normalizeImageBackground(srcData?.imageUrl),
      },
    },
  ],
})
